import {
  PLAYER_STAT_TYPES,
  type StatColumn,
  type StatTypeConfig
} from './columns';

export type CustomStatCategory = {
  label: string;
  options: StatColumn[];
};

/**
 * The catalogue is harvested from the player tab configs, thematic tabs
 * first so every column lands in its natural category. The Key Stats tab
 * goes last because it mostly repeats columns from the thematic tabs; its
 * unique remainder is the goal involvement trio.
 */
const CATALOGUE_SOURCES: { statType: string; category: string }[] = [
  { statType: 'shooting', category: 'Goal threat' },
  { statType: 'creativity', category: 'Creativity' },
  { statType: 'passing', category: 'Passing' },
  { statType: 'involvement', category: 'Involvement' },
  { statType: 'setpieces', category: 'Set pieces' },
  { statType: 'defending', category: 'Defending' },
  { statType: 'defensive', category: 'Defensive' },
  { statType: 'fpl', category: 'FPL' },
  { statType: 'attack', category: 'Goal involvement' }
];

const buildCustomStatsCatalogue = (): CustomStatCategory[] => {
  const seenPaths = new Set<string>();
  const categories: CustomStatCategory[] = [];

  for (const { statType, category } of CATALOGUE_SOURCES) {
    const config = PLAYER_STAT_TYPES[statType];
    if (!config) continue;

    const options = config.groups
      .flatMap((group) => group.columns)
      .filter((column) => {
        if (seenPaths.has(column.path)) return false;
        seenPaths.add(column.path);
        return true;
      });

    if (options.length > 0) {
      categories.push({ label: category, options });
    }
  }

  return categories;
};

export const CUSTOM_STATS_CATALOGUE = buildCustomStatsCatalogue();

const KNOWN_CATALOGUE_PATHS = new Set(
  CUSTOM_STATS_CATALOGUE.flatMap((category) =>
    category.options.map((option) => option.path)
  )
);

// Participation is served on every stats response, so it never needs to be
// requested as a field group.
const PARTICIPATION_GROUP = 'participation';
const FALLBACK_FIELDS = 'fpl';

/** Field groups are the first segment of each column's dot-path. */
const fieldsForPaths = (paths: string[]): string => {
  const groups = new Set<string>();
  for (const path of paths) {
    const [group] = path.split('.');
    if (group && group !== PARTICIPATION_GROUP) {
      groups.add(group);
    }
  }
  return groups.size > 0 ? [...groups].join(',') : FALLBACK_FIELDS;
};

/**
 * Builds a table config from the selected column paths. Columns render in
 * catalogue order rather than selection order, matching the legacy custom
 * table. Returns undefined when nothing is selected so the page can prompt
 * for a selection instead of querying.
 */
export const buildCustomStatsConfig = (
  selectedPaths: string[]
): StatTypeConfig | undefined => {
  if (selectedPaths.length === 0) return undefined;

  const selected = new Set(selectedPaths);
  const groups = CUSTOM_STATS_CATALOGUE.map((category) => ({
    label: category.label,
    columns: category.options.filter((option) => selected.has(option.path))
  })).filter((group) => group.columns.length > 0);

  if (groups.length === 0) return undefined;

  const paths = groups.flatMap((group) =>
    group.columns.map((column) => column.path)
  );
  return { fields: fieldsForPaths(paths), groups };
};

export const OPTA_CUSTOM_STATS_STORAGE_KEY = 'ffh-opta-custom-stats';

export const loadStoredCustomStatPaths = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(OPTA_CUSTOM_STATS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Drop anything the catalogue no longer serves so a stale stored
    // selection cannot produce dead columns.
    return parsed.filter(
      (path): path is string =>
        typeof path === 'string' && KNOWN_CATALOGUE_PATHS.has(path)
    );
  } catch {
    return [];
  }
};

export const storeCustomStatPaths = (paths: string[]): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      OPTA_CUSTOM_STATS_STORAGE_KEY,
      JSON.stringify(paths)
    );
  } catch {
    // Storage can be unavailable (private browsing, quota); the selection
    // then simply lasts for the session.
  }
};
